import { queryOptions } from '@tanstack/react-query'

import { apiClient } from './client'
import type {
  Branch,
  MyBranches,
  MyBranchesPayload,
  BusinessDay,
  BusinessDayPayload,
  PagedResult,
} from './types'

/**
 * Just the branches, for the pickers that need a list and nothing else.
 *
 * Derived from the same cached query rather than a second request, so a form and the header can
 * never disagree about which branches exist.
 */
export const branchListQueryOptions = () => ({
  ...myBranchesQueryOptions(),
  select: (data: MyBranches): Branch[] => (Array.isArray(data?.items) ? data.items : []),
})

/**
 * Branches this user may operate in. Most staff get exactly one — their home branch — and the
 * branch selector stays hidden for them. Head-office and audit roles get several.
 */
/** Answer used when the endpoint is unreachable or answers a shape this client does not know. */
const EMPTY_BRANCHES: MyBranches = {
  items: [],
  homeBranchId: '',
  canAccessAllBranches: false,
  directoryAvailable: false,
}

export const myBranchesQueryOptions = () =>
  queryOptions({
    queryKey: ['reference', 'my-branches'] as const,
    queryFn: async () => {
      const payload = (await apiClient.get<MyBranchesPayload>('/access/me/branches')).data

      // Normalised and narrowed here rather than trusted. The identifier arrives as a number and is
      // a string everywhere it is used — session storage, a request header — and this endpoint once
      // answered with a wrapped object while the client expected a bare array, which turned a
      // reference lookup into a blank screen behind the whole header.
      if (!Array.isArray(payload?.items)) {
        return EMPTY_BRANCHES
      }

      return {
        items: payload.items.map(
          (branch): Branch => ({
            id: String(branch.branchId),
            code: branch.code,
            name: branch.name,
            isHomeBranch: branch.isHomeBranch,
            accessExpiresAtUtc: branch.accessExpiresAtUtc,
          }),
        ),
        homeBranchId: String(payload.homeBranchId),
        canAccessAllBranches: payload.canAccessAllBranches ?? false,
        directoryAvailable: payload.directoryAvailable ?? false,
      }
    },
    // Branch access changes only when an administrator changes it, and that invalidates the
    // session's permission snapshot anyway.
    staleTime: 30 * 60 * 1000,
  })

/**
 * The open business day for a branch. This is the date every posting lands on, and it is not the
 * browser's date: a branch that has not yet closed yesterday is still working in yesterday.
 */
export const businessDayQueryOptions = (branchId: string | null) =>
  queryOptions({
    queryKey: ['ledger', 'business-day', branchId] as const,
    queryFn: async () => {
      const payload = (
        await apiClient.get<BusinessDayPayload>('/ledger/business-day/current', {
          params: { branchId },
        })
      ).data

      // The identifier is a string everywhere this application uses one; the ledger sends a number.
      return { ...payload, branchId: String(payload.branchId) } satisfies BusinessDay
    },
    enabled: Boolean(branchId),
    staleTime: 60 * 1000,
  })

/**
 * A member of staff, as the Access module's directory serves them. This is the summary shape
 * behind `/access/users`, not the full user record.
 */
export interface StaffSummary {
  userId: string
  username: string
  fullName: string
  employeeCode: string | null
  /** Serialised as a number on this endpoint, unlike the string branch ids groups carry. */
  branchId: number | string
  isLoanOfficer: boolean
  isTeller: boolean
}

/**
 * The loan officers a group may be assigned to, at one branch.
 *
 * It reads the Access directory, which is gated on `admin.user.view` — so the question worth
 * asking before relying on it is who can satisfy that on day one. Every role that holds
 * `groups.officer.assign` (BranchManager, SuperAdministrator) also holds `admin.user.view`, so an
 * officer-assignment dialog cannot be opened by someone who is then unable to see the list of
 * officers to choose from. If that ever stops being true, this query fails with a 403 the dialog
 * surfaces rather than an empty select that looks like "no officers exist".
 *
 * The `isLoanOfficer` flag is filtered here rather than asked for, because the endpoint has no
 * parameter for it. The server refuses a non-officer anyway (`groups.officer_not_a_loan_officer`);
 * this only keeps the list to people the assignment could succeed for.
 */
export const loanOfficersQueryOptions = (branchId: string | null) =>
  queryOptions({
    queryKey: ['reference', 'loan-officers', branchId] as const,
    queryFn: async () => {
      const response = await apiClient.get<PagedResult<StaffSummary>>('/access/users', {
        params: { branchId, page: 1, pageSize: 200 },
      })

      return response.data.items.filter((staff) => staff.isLoanOfficer)
    },
    enabled: Boolean(branchId),
    staleTime: 5 * 60 * 1000,
  })
